/**
 * What a submission's kind means, for readers on both sides of the wire.
 *
 * Its own module rather than a member of `submission-service`, which imports
 * the database and so cannot be reached from a browser bundle. Everything here
 * is a pure function of the enum, and the coach's Review tab, the coach's
 * card-details modal and the client's feedback list all read it.
 */
import type { SubmissionKind } from '../../db/schema/enums.js'

/**
 * Whether this submission is a card the client ticked rather than work they
 * handed in (LG-905).
 *
 * Nothing that describes an artifact applies to one: it has no language, no
 * run, no duration, and no attempt number, because nobody attempted anything —
 * they read the chapter and said so. Its `submissionNumber` counts tick /
 * un-tick cycles, so rendering it as "Attempt 3" or "Submission 3" says
 * something untrue about the client.
 *
 * Takes a bare `string`, and it does cost something: three of the callers read
 * the kind off the wire, where a browser tab open across a deploy can hold a
 * value this bundle has never heard of, so the parameter has to admit one —
 * and that admits `isTickedCard(review.status)` too, which compiles, always
 * returns `false`, and silently reverts the wording this exists for. The
 * `SubmissionKind | (string & {})` spelling does *not* buy that check back:
 * `string & {}` reduces to `string`, so it preserves autocomplete and nothing
 * else. `completesOnSubmission` can refuse `string` because both its callers
 * hold the real enum; this one cannot.
 *
 * The literal is pinned to the enum, so a rename is at least a compile error
 * here rather than a predicate that quietly stops matching.
 */
export function isTickedCard(kind: string): boolean {
  return kind === ('manual' satisfies SubmissionKind)
}
